import { useSyncExternalStore } from 'react'
import { refreshScheme } from './colorScheme'
import { applyGround, clearGround, groundFromDocAndStore, readStoredGround } from './groundMode'
import {
  UI_PALETTE_STORAGE_KEY,
  UI_THEME_STORAGE_KEY,
  decideRemoteApply,
  paletteFromRestDoc,
  parseUiPalette,
  parseUiTheme,
  readUrlUiPalette,
  readUrlUiTheme,
  themeFromRestDoc,
  type UiPalette,
  type UiTheme,
} from './uiThemeCore'

export type { UiPalette, UiTheme } from './uiThemeCore'

const listeners = new Set<() => void>()
let activeStatesReady = false

/** iOS ต้องมี touchstart listener ถึงจะยิง :active — ใส่เฉพาะธีม glass (classic ไม่เปลี่ยนพฤติกรรม) */
function ensureActiveStates(theme: UiTheme) {
  if (theme !== 'glass' || activeStatesReady) return
  activeStatesReady = true
  document.addEventListener('touchstart', () => {}, { passive: true })
}

export function getUiTheme(): UiTheme {
  return parseUiTheme(document.documentElement.dataset.ui) ?? 'classic'
}

export function setUiTheme(theme: UiTheme): void {
  if (getUiTheme() === theme) return
  document.documentElement.dataset.ui = theme
  ensureActiveStates(theme)
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useUiTheme(): UiTheme {
  return useSyncExternalStore(subscribe, getUiTheme, () => 'classic')
}

/**
 * ใช้ค่าธีมจาก Firestore — อ่านผ่าน REST ตัวเดียวกับชุดสี (fetchUiDoc · ยิงพร้อมกันได้ request เดียว)
 * เดิมรอ window.__earlyUiThemePromise จาก ordercus-firebase.js แต่ถูกถอดไปพร้อมระบบสิทธิ์ออร์เดอร์ (12 ต.ค. 2569)
 * → เครื่องที่ไม่เคยจำ oc-ui ค้าง classic ตลอด · ไม่รอ ไม่บล็อกการแสดงออร์เดอร์ · เช็คครั้งเดียวต่อ session
 */
export function startRemoteUiThemeSync(isOrderVisible: () => boolean): void {
  ensureActiveStates(getUiTheme())
  const urlTheme = readUrlUiTheme(window.location.search)
  if (urlTheme) return
  try {
    if (sessionStorage.getItem(THEME_SESSION_KEY)) return
  } catch {
    /* private mode — เช็คทุกครั้งที่เปิดหน้า */
  }
  fetchUiDoc()
    .then((doc) => {
      const remote = themeFromRestDoc(doc)
      if (!remote) return
      const { apply, store } = decideRemoteApply({
        urlTheme,
        current: getUiTheme(),
        remote,
        orderVisible: isOrderVisible(),
      })
      try {
        if (store) localStorage.setItem(UI_THEME_STORAGE_KEY, store)
        sessionStorage.setItem(THEME_SESSION_KEY, '1')
      } catch {
        /* private mode / quota */
      }
      if (apply) setUiTheme(apply)
    })
    .catch(() => {
      /* ออฟไลน์/ช้า — คงธีมปัจจุบัน */
    })
}

/* ---------- ชุดสี (data-tone บน <html>) ---------- */

/** doc สาธารณะ (rules: read if true) — อ่านผ่าน REST ไม่ต้องโหลด Firebase SDK/ล็อกอิน · 1 read ต่อ session */
const UI_DOC_REST_URL =
  'https://firestore.googleapis.com/v1/projects/srikanett-order/databases/(default)/documents/publicIntegrations/ordercusUi'
const PALETTE_SESSION_KEY = 'oc-tone-checked'
const THEME_SESSION_KEY = 'oc-ui-checked'

let uiDocPromise: Promise<unknown> | null = null
/** อ่าน doc ordercusUi ครั้งเดียวต่อการเปิดหน้า — ธีม + ชุดสีใช้ผลเดียวกัน (ไม่เพิ่มการอ่าน) */
export function fetchUiDoc(): Promise<unknown> {
  if (!uiDocPromise) {
    const controller = typeof AbortController === 'undefined' ? null : new AbortController()
    const timer = window.setTimeout(() => controller?.abort(), 6000)
    uiDocPromise = fetch(UI_DOC_REST_URL, { signal: controller?.signal, cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .finally(() => window.clearTimeout(timer))
    uiDocPromise.catch(() => {
      uiDocPromise = null // พลาด → ครั้งหน้าลองใหม่
    })
  }
  return uiDocPromise
}

export function getUiPalette(): UiPalette {
  return parseUiPalette(document.documentElement.dataset.tone) ?? 'royal'
}

export function setUiPalette(palette: UiPalette, groundDoc?: unknown): void {
  const cur = getUiPalette()
  if (palette === 'ground') {
    // ธีมนี้มีค่าตั้งเอง (preset/สีกำหนดเอง) — ตั้งซ้ำได้แม้ palette เดิมเป็น ground เพื่อรับค่าใหม่
    document.documentElement.dataset.tone = 'ground'
    applyGround(groundDoc === undefined ? readStoredGround() : groundFromDocAndStore(groundDoc))
    return
  }
  if (cur === palette) return
  if (cur === 'ground') {
    clearGround()
    refreshScheme()
  }
  if (palette === 'royal') delete document.documentElement.dataset.tone
  else document.documentElement.dataset.tone = palette
}

/** เปิดหน้าด้วย data-tone="ground" จากแคช (inline script ใน index.html) — วาดพื้นหลังทันที */
export function initGroundFromDom(): void {
  if (getUiPalette() === 'ground') applyGround(readStoredGround())
}

/**
 * ซิงก์ชุดสีจากแอดมิน — ?tone= ชนะเสมอ · ระหว่างแสดงออร์เดอร์ไม่สลับสีกลางหน้า (จำไว้ใช้ครั้งหน้า)
 * เช็ค Firestore ครั้งเดียวต่อ session (sessionStorage) กันยิงซ้ำทุกครั้งที่เปลี่ยนหน้า
 */
export function startRemoteUiPaletteSync(isOrderVisible: () => boolean): void {
  if (readUrlUiPalette(window.location.search)) return
  try {
    if (sessionStorage.getItem(PALETTE_SESSION_KEY)) return
  } catch {
    /* private mode — เช็คทุกครั้งที่เปิดหน้า */
  }
  fetchUiDoc()
    .then((doc) => {
      const remote = paletteFromRestDoc(doc)
      try {
        localStorage.setItem(UI_PALETTE_STORAGE_KEY, remote)
        sessionStorage.setItem(PALETTE_SESSION_KEY, '1')
      } catch {
        /* private mode / quota */
      }
      if (remote === 'ground') groundFromDocAndStore(doc) // เก็บค่าไว้ใช้ครั้งหน้าแม้ตอนนี้ไม่สลับกลางออร์เดอร์
      if (!isOrderVisible()) setUiPalette(remote, remote === 'ground' ? doc : undefined)
    })
    .catch(() => {
      /* ออฟไลน์/ช้า — คงชุดสีเดิม */
    })
}
